/**
 * Tooltip Manager - Moon Moon Agency
 * Repositionne les bulles de tooltip pour éviter le débordement du viewport
 * (mobile Safari : bulle centrée sur un trigger proche d'un bord se faisait couper).
 *
 * La bulle reste centrée sur le trigger (CSS : translateX(-50%)). À l'affichage,
 * on calcule le débordement à partir de la géométrie stable (trigger + largeur
 * intrinsèque de la bulle, sans dépendre de la transition en cours) et on applique
 * un décalage borné (--mm-tooltip-shift), en recalant la flèche
 * (--mm-tooltip-arrow-shift) pour qu'elle reste sous le trigger.
 */

/*********************************************************************************************
 * 🔧 TOOLTIP MANAGER
 */
const TooltipManager = {
	config: {
		selectors: {
			trigger: '[data-css-tooltip-hover]',
			anchor: '.mm-interrogation-point-icon-container',
			container: '.mm-tooltip-bubble-container',
			bubble: '.mm-tooltip-bubble',
			arrow: '.mm-bubble-arrow',
		},
		margin: 8, // marge mini entre la bulle et le bord du viewport (px)
	},

	init() {
		this._bindEvents();
	},

	_bindEvents() {
		// Une seule activation par trigger : on recalcule à chaque ouverture
		// (pointerenter pour le hover desktop, focusin pour tap/clavier mobile).
		document.addEventListener('pointerenter', (e) => this._onActivate(e), true);
		document.addEventListener('focusin', (e) => this._onActivate(e), true);
	},

	_onActivate(e) {
		const target = e.target;
		if (!target.closest) return;
		const trigger = target.closest(this.config.selectors.trigger);
		if (trigger) this.reposition(trigger);
	},

	reposition(trigger) {
		const container = trigger.querySelector(this.config.selectors.container);
		const bubble = trigger.querySelector(this.config.selectors.bubble);
		const arrow = trigger.querySelector(this.config.selectors.arrow);
		if (!container || !bubble) return;

		// La bulle est ancrée sur l'icône "?" (point de référence visuel), pas sur
		// le trigger entier : ce dernier peut couvrir un paragraphe multi-ligne, ce
		// qui décalerait la bulle loin de l'icône.
		const anchor = trigger.querySelector(this.config.selectors.anchor) || trigger;

		// Géométrie stable, indépendante de la transition/shift en cours :
		// - centre de l'ancre dans le viewport
		// - largeur réelle de la bulle (offsetWidth n'est pas affecté par le transform)
		const anchorRect = anchor.getBoundingClientRect();
		const anchorCenter = anchorRect.left + anchorRect.width / 2;
		const bubbleWidth = bubble.offsetWidth;

		// Bords théoriques de la bulle centrée sur l'ancre (avant tout shift).
		const left = anchorCenter - bubbleWidth / 2;
		const right = anchorCenter + bubbleWidth / 2;
		const vw = document.documentElement.clientWidth;
		const margin = this.config.margin;

		let shift = 0;
		if (left < margin) {
			shift = margin - left; // pousser vers la droite
		} else if (right > vw - margin) {
			shift = vw - margin - right; // pousser vers la gauche
		}

		container.style.setProperty('--mm-tooltip-shift', shift + 'px');
		// La flèche compense le décalage pour rester pile sous le trigger.
		if (arrow) arrow.style.setProperty('--mm-tooltip-arrow-shift', -shift + 'px');
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => TooltipManager.init());
} else {
	TooltipManager.init();
}
